import { useQuery } from '@tanstack/react-query'
import { apiPaths, meSchema, type Capability, type Me } from '@/api/schema'
import { request } from '@/lib/http'
import { useAuthSession } from './session'

export const meQueryKey = ['me'] as const

/** Perfil + capabilities do usuário (GET /api/v2/me). O backend é a autoridade (DEC-004). */
export function useMe() {
  const session = useAuthSession()
  return useQuery<Me>({
    queryKey: meQueryKey,
    queryFn: ({ signal }) => request(apiPaths.me, { schema: meSchema, signal }),
    enabled: session.status === 'authenticated',
    staleTime: 5 * 60_000,
  })
}

export interface Permissions {
  /** true enquanto /me não respondeu (não decidir acesso ainda). */
  isLoading: boolean
  error: unknown
  capabilities: ReadonlySet<Capability>
  can: (permission: Capability | Capability[] | undefined) => boolean
}

export function hasPermission(
  capabilities: ReadonlySet<Capability>,
  permission: Capability | Capability[] | undefined,
): boolean {
  if (!permission) return true
  const required = Array.isArray(permission) ? permission : [permission]
  return required.every((item) => capabilities.has(item))
}

export function usePermissions(): Permissions {
  const me = useMe()
  const capabilities = new Set<Capability>(me.data?.capabilities ?? [])
  return {
    isLoading: me.isPending,
    error: me.error,
    capabilities,
    can: (permission) => hasPermission(capabilities, permission),
  }
}

/** Atalho: o usuário tem a(s) capability(ies)? Falso enquanto /me carrega. */
export function usePermission(permission: Capability | Capability[] | undefined): boolean {
  return usePermissions().can(permission)
}
